import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, SimpleGrid, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  FunnelSimple,
  Copy,
  Buildings,
  FileX,
  ArrowDown,
  ArrowUp,
  FileXls,
  CaretRight,
  X,
} from 'phosphor-react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { useNotasDuplicadas } from '../../../../hooks/useNotasDuplicadas';
import { formatCNPJ } from '../../../../utils/FormatFunctions';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import { SearchModeBar, SearchModeOption } from '../../../../components/SearchModeBar';
import { ISelect } from '../../../../types/shared';

moment.locale('pt-br');

type TipoNotaFilter = 'todos' | 'entrada' | 'saida';
type SearchMode = 'empresa' | 'cnpj' | 'id';

const SEARCH_MODES: SearchModeOption[] = [
  {
    key: 'empresa',
    buttonLabel: 'Empresa',
    menuLabel: 'Empresa',
    placeholder: 'Buscar por empresa…',
  },
  { key: 'cnpj', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Buscar por CNPJ…' },
  { key: 'id', buttonLabel: 'Código', menuLabel: 'Código', placeholder: 'Buscar por código…' },
];

interface FilterFormValues {
  tipo_nota: ISelect<string> | null;
  clifor: ISelect<string> | null;
}

/** Cartão de número do topo; com `onClick`, vira filtro (Entrada / Saída). */
const Kpi = ({
  rotulo,
  valor,
  dica,
  icone,
  cor,
  ativo,
  onClick,
}: {
  rotulo: string;
  valor: number;
  dica: string;
  icone: React.ReactNode;
  cor: string;
  ativo?: boolean;
  onClick?: () => void;
}) => {
  const cartao = (
    <GlassCard hoverable={!!onClick} destacado={ativo} intensity="strong" p="14px 16px">
      <Flex align="center" gap={3}>
        <Flex
          w="38px"
          h="38px"
          borderRadius="12px"
          align="center"
          justify="center"
          flex="none"
          bg={`${cor}1A`}
          color={cor}
        >
          {icone}
        </Flex>
        <Box minW={0}>
          <Text fontSize="11px" color="#64748B" fontWeight={600}>
            {rotulo}
          </Text>
          <Text fontSize="22px" fontWeight={800} color="#0F172A" lineHeight="1.1">
            {valor.toLocaleString('pt-BR')}
          </Text>
        </Box>
        {onClick && (
          <Text ml="auto" fontSize="10.5px" color={ativo ? cor : '#94A3B8'} fontWeight={700}>
            {ativo ? 'filtrando' : 'filtrar'}
          </Text>
        )}
      </Flex>
    </GlassCard>
  );
  return (
    <Tooltip label={dica} fontSize="11px" hasArrow placement="top" maxW="260px">
      {onClick ? (
        <Box
          as="button"
          type="button"
          onClick={onClick}
          textAlign="left"
          w="full"
          aria-pressed={!!ativo}
        >
          {cartao}
        </Box>
      ) : (
        <Box w="full">{cartao}</Box>
      )}
    </Tooltip>
  );
};

export function ModulesHomeIntegrationDuplicateNotesPage() {
  const navigate = useNavigate();
  const { getInsights, getEmpresasResumo, getClifores, exportAllDuplicadas } = useNotasDuplicadas();
  const [isExporting, setIsExporting] = useState(false);
  const [tipoNotaFilter, setTipoNotaFilter] = useState<TipoNotaFilter>('todos');
  const [cliforFilter, setCliforFilter] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>('empresa');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const {
    currentDate: currentFilterDate,
    dateStart,
    dateEnd,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
  } = useMonthFilter({ additionalParams: {} });

  const filtersFormMethods = useForm<FilterFormValues>({
    defaultValues: {
      tipo_nota: null,
      clifor: null,
    },
  });

  // Debounce para o campo de busca (aguarda 500ms após parar de digitar)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInputValue);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchInputValue]);

  // Filtros base (data) - sem tipo_nota e sem search (para insights gerais)
  const baseFiltersObject = useMemo(
    () => cleanFilters(Object.fromEntries([...Array.from(filters)])),
    [filters],
  );

  // Filtros para insights (com tipo_nota)
  const insightsFiltersObject = useMemo(() => {
    const base = { ...baseFiltersObject };
    if (tipoNotaFilter !== 'todos') {
      return { ...base, tipo_nota: tipoNotaFilter === 'entrada' ? 'E' : 'A' };
    }
    return base;
  }, [baseFiltersObject, tipoNotaFilter]);

  // Filtros para empresas (com tipo_nota + search + search_field + clifor)
  const empresasFiltersObject = useMemo(() => {
    const obj: Record<string, any> = { ...insightsFiltersObject };
    if (searchTerm) {
      obj.search = searchTerm;
      obj.search_field = searchMode;
    }
    if (cliforFilter) {
      obj.clifor = cliforFilter;
    }
    return obj;
  }, [insightsFiltersObject, searchTerm, searchMode, cliforFilter]);

  // Query para insights (sempre busca contagens por tipo)
  const { data: insightsData, isFetching: isFetchingInsights } = useQuery({
    queryKey: ['notasDuplicadasInsights', baseFiltersObject],
    queryFn: getInsights,
  });

  // Query para lista de empresas com duplicadas (server-side search + tipo_nota + clifor)
  const { data: empresasResumoData, isFetching: isFetchingEmpresas } = useQuery({
    queryKey: ['notasDuplicadasEmpresas', empresasFiltersObject],
    queryFn: getEmpresasResumo,
  });

  // Query para lista de clifores (para o filtro avançado)
  const { data: cliforesData } = useQuery({
    queryKey: ['notasDuplicadasClifores', baseFiltersObject],
    queryFn: getClifores,
  });

  const insights = insightsData?.data;
  const empresasResumo = empresasResumoData?.data || [];

  // Opções de clifores para o select
  const cliforOptions = useMemo(() => {
    const clifores = cliforesData?.data || [];
    return clifores.map(c => ({
      value: c.nome,
      label: c.nome,
    }));
  }, [cliforesData]);

  // Contadores filtrados para os cards selecionáveis
  const hasActiveFilters = tipoNotaFilter !== 'todos' || cliforFilter !== null;
  const activeFiltersCount = (tipoNotaFilter !== 'todos' ? 1 : 0) + (cliforFilter !== null ? 1 : 0);

  const handleEmpresaClick = (codiEmp: number) => {
    navigate({
      pathname: `/modulesHome/integration/duplicateNotes/${codiEmp}`,
      search: createSearchParams({
        date_start: dateStart || moment().startOf('month').format('YYYY-MM-DD'),
        date_end: dateEnd || moment().endOf('month').format('YYYY-MM-DD'),
      }).toString(),
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportAllDuplicadas({
        dateStart: dateStart || undefined,
        dateEnd: dateEnd || undefined,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    if (values.tipo_nota?.value) {
      setTipoNotaFilter(values.tipo_nota.value === 'E' ? 'entrada' : 'saida');
    } else {
      setTipoNotaFilter('todos');
    }
    setCliforFilter(values.clifor?.value || null);
  };

  const handleClearFilters = () => {
    filtersFormMethods.setValue('tipo_nota', null);
    filtersFormMethods.setValue('clifor', null);
    setTipoNotaFilter('todos');
    setCliforFilter(null);
  };

  const limparTodosFiltros = () => {
    setTipoNotaFilter('todos');
    setCliforFilter(null);
    setSearchInputValue('');
    setSearchTerm('');
    filtersFormMethods.setValue('tipo_nota', null);
    filtersFormMethods.setValue('clifor', null);
  };

  const totalFiltros =
    (tipoNotaFilter !== 'todos' ? 1 : 0) + (cliforFilter ? 1 : 0) + (searchTerm ? 1 : 0);

  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} py={5}>
        {/* Cabeçalho no padrão da Conferência */}
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/integration" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #F59E0B, #EF4444)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(239,68,68,0.45)"
          >
            <Copy size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Integração › Notas Duplicadas
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em">
              Notas Duplicadas
            </Text>
          </Box>
          <Box flex={1} />
          {(isFetchingInsights || isFetchingEmpresas) && (
            <Flex role="status" aria-live="polite" align="center" gap={2}>
              <Spinner size="sm" color="#F59E0B" />
              <Text fontSize="13px" color="#64748B">
                Carregando…
              </Text>
            </Flex>
          )}
          <FormProvider {...filtersFormMethods}>
            <MonthNavigator
              currentDate={currentFilterDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
              compact
            />
          </FormProvider>
        </Flex>

        {/* Números do mês; Entrada e Saída também filtram a lista */}
        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4}>
          <Kpi
            rotulo="Empresas com duplicadas"
            valor={insights?.total_empresas_com_duplicadas ?? 0}
            dica="Empresas com pelo menos uma nota lançada mais de uma vez no Domínio neste mês."
            icone={<Buildings size={20} weight="duotone" />}
            cor="#DC2626"
          />
          <Kpi
            rotulo="Lançamentos duplicados"
            valor={insights?.total_notas_duplicadas ?? 0}
            dica="Lançamentos que fazem parte de algum grupo de duplicadas neste mês. Uma nota lançada duas vezes conta 2."
            icone={<FileX size={20} weight="duotone" />}
            cor="#EA580C"
          />
          <Kpi
            rotulo="Entrada"
            valor={insights?.total_entrada ?? 0}
            dica="Clique para ver só as notas de entrada."
            icone={<ArrowDown size={20} weight="bold" />}
            cor="#1d4ed8"
            ativo={tipoNotaFilter === 'entrada'}
            onClick={() => setTipoNotaFilter(prev => (prev === 'entrada' ? 'todos' : 'entrada'))}
          />
          <Kpi
            rotulo="Saída"
            valor={insights?.total_saida ?? 0}
            dica="Clique para ver só as notas de saída (serviços prestados)."
            icone={<ArrowUp size={20} weight="bold" />}
            cor="#047857"
            ativo={tipoNotaFilter === 'saida'}
            onClick={() => setTipoNotaFilter(prev => (prev === 'saida' ? 'todos' : 'saida'))}
          />
        </SimpleGrid>

        {/* Busca e ações */}
        <GlassCard intensity="strong" p="12px 14px">
          <Flex
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
            gap={3}
            direction={{ base: 'column', md: 'row' }}
          >
            <Box w={{ base: 'full', md: 'auto' }}>
              <SearchModeBar
                modes={SEARCH_MODES}
                mode={searchMode}
                onModeChange={m => setSearchMode(m as SearchMode)}
                value={searchInputValue}
                onValueChange={setSearchInputValue}
              />
            </Box>
            <Flex align="center" gap={2} justify={{ base: 'space-between', md: 'flex-end' }}>
              {totalFiltros > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  borderRadius="10px"
                  rightIcon={<X size={13} weight="bold" />}
                  onClick={limparTodosFiltros}
                >
                  {totalFiltros === 1 ? '1 filtro' : `${totalFiltros} filtros`}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                borderRadius="10px"
                leftIcon={<FunnelSimple size={16} />}
                colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                onClick={() => setIsFilterDrawerOpen(true)}
              >
                Filtros{hasActiveFilters ? ` (${activeFiltersCount})` : ''}
              </Button>
              <Tooltip label="Exportar as notas duplicadas do mês para Excel" placement="top">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="green"
                  borderRadius="10px"
                  leftIcon={<FileXls size={17} weight="duotone" />}
                  onClick={handleExport}
                  isLoading={isExporting}
                  loadingText="Exportando…"
                  isDisabled={empresasResumo.length === 0}
                >
                  Excel
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        </GlassCard>

        {/* Empresas */}
        {isFetchingEmpresas && empresasResumo.length === 0 && (
          <Flex direction="column" align="center" gap={3} py={12}>
            <Spinner size="lg" color="#F59E0B" thickness="3px" />
            <Text fontSize="13px" color="#64748B">
              Carregando empresas…
            </Text>
          </Flex>
        )}
        {!isFetchingEmpresas && empresasResumo.length === 0 && (
          <GlassCard intensity="medium" p={10}>
            <Flex direction="column" align="center" gap={2}>
              <FileX size={40} color="#CBD5E1" />
              <Text fontSize="13px" color="#64748B">
                {searchTerm
                  ? 'Nenhuma empresa encontrada para a busca.'
                  : 'Nenhuma nota duplicada encontrada no mês.'}
              </Text>
            </Flex>
          </GlassCard>
        )}
        {empresasResumo.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {empresasResumo.map(item => {
              const empresaNome = item.razao_social || item.nome_empresa || '-';
              const entrada = item.quantidade_entrada ?? 0;
              const saida = item.quantidade_saida ?? 0;
              const total = Math.max(item.quantidade_duplicadas, 1);
              return (
                <Box
                  key={item.codi_emp}
                  as="button"
                  type="button"
                  textAlign="left"
                  w="full"
                  onClick={() => handleEmpresaClick(item.codi_emp)}
                >
                  <GlassCard hoverable intensity="strong" p="16px 18px">
                    <Flex justify="space-between" align="center" mb={1}>
                      <Text
                        fontSize="10.5px"
                        fontWeight={700}
                        color="#64748B"
                        bg="rgba(15,23,42,0.05)"
                        px="7px"
                        py="2px"
                        borderRadius="6px"
                      >
                        ID {item.codi_emp}
                      </Text>
                      <Text fontSize="11px" color="#94A3B8" fontFamily="mono">
                        {item.cnpj ? formatCNPJ(item.cnpj) : '—'}
                      </Text>
                    </Flex>
                    <Text
                      fontSize="15px"
                      fontWeight={700}
                      color="#0F172A"
                      noOfLines={1}
                      title={empresaNome}
                    >
                      {empresaNome}
                    </Text>
                    <Flex align="baseline" gap={2} mt={3}>
                      <Text fontSize="26px" fontWeight={800} color="#DC2626" lineHeight="1">
                        {item.quantidade_duplicadas}
                      </Text>
                      <Text fontSize="12px" color="#64748B">
                        lançamento{item.quantidade_duplicadas === 1 ? '' : 's'} duplicado
                        {item.quantidade_duplicadas === 1 ? '' : 's'}
                      </Text>
                      <Box ml="auto" color="#94A3B8">
                        <CaretRight size={16} weight="bold" />
                      </Box>
                    </Flex>
                    {/* Barra entrada x saída */}
                    <Flex
                      h="6px"
                      borderRadius="999px"
                      overflow="hidden"
                      bg="rgba(15,23,42,0.06)"
                      mt={3}
                    >
                      <Box w={`${(entrada / total) * 100}%`} bg="#3B82F6" />
                      <Box w={`${(saida / total) * 100}%`} bg="#10B981" />
                    </Flex>
                    <Flex gap={4} mt={2} fontSize="11.5px" color="#475569">
                      <Flex align="center" gap="5px">
                        <Box w="8px" h="8px" borderRadius="999px" bg="#3B82F6" />
                        {entrada} entrada
                      </Flex>
                      <Flex align="center" gap="5px">
                        <Box w="8px" h="8px" borderRadius="999px" bg="#10B981" />
                        {saida} saída
                      </Flex>
                    </Flex>
                  </GlassCard>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Flex>

      {/* Drawer de Filtros Avançados */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        title="Filtros Avançados"
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onApplyFilters={handleApplyFilters}
        formMethods={filtersFormMethods}
        size="md"
      >
        <div className="flex flex-col gap-4">
          <SelectInputForm<ISelect<string>>
            name="tipo_nota"
            placeholder="Filtrar por Tipo de Nota"
            options={[
              { value: 'E', label: 'Entrada' },
              { value: 'A', label: 'Saída' },
            ]}
            controlMb={0}
            isClearable
          />
          <SelectInputForm<ISelect<string>>
            name="clifor"
            placeholder="Filtrar por Cliente/Fornecedor"
            options={cliforOptions}
            controlMb={0}
            isClearable
          />
        </div>
      </FilterDrawer>
    </GlassPageBg>
  );
}
